"use client";

import React, { useEffect, useState, useTransition } from "react";
import ReactDOM from "react-dom";

import Image from "next/image";
import LoadingDots from "../../Atoms/LoadingDots";
import { FilledHeart, EmptyHeart } from "../../../public/Theme";
import QuickAddToCartOverlay from "../QuickAddToCartOverlay";
import QuickAddToCartPopup from "../QuickAddToCartPopup";
import { getProductVariants } from "../../../lib/Products/GetProducts";
import TypographyPoppins from "@/components/Atoms/TypographyPoppins";
import Heading from "@/components/Atoms/TypographyPoppins";
import { formatPrice } from "@/components/utils/utils";
import NewPrimaryButton from "@/components/Atoms/Buttons/NewPrimaryButton";
import { quickAddToCartFromOverlay } from "../QuickAddToCartOverlay/constants";
import {
    LIMITED_STOCK_MESSAGE,
    UPDATE_CART_SUCCESSFULLY,
} from "@/public/Constants/EnumConstants";
import { toast } from "react-toastify";
import ProductCarousel from "../ProductCarousel";
import LowStockIndicator from "../LowStockIndicator";

const QuickAddToCartPortal = ({ children }) => {
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
    }, []);

    if (!mounted) return null;

    return ReactDOM.createPortal(children, document.body);
};

const QuickAddToCartButton = ({
    icon,
    currencyCode,
    price,
    productName,
    productId,
    sizeStandard,
    brandName,
    imagesCollection,
    salesPrice,
    isWishListItem,
    addProductToWishList,
    removeProductToWishList,
    showCarousel,
    totalStockOfVariants,
}) => {
    const [variants, setVariants] = useState(false);
    const [showVariants, setShowVariants] = useState(false);
    const [variantsLoading, setVariantsLoading] = useState(false);
    const [selectedVariant, setSelectedVariant] = useState(null);
    const [variantsOutOfStock, setVariantsOutOfStock] = useState([]);
    const [isPending, startTransition] = useTransition();

    const fetchVariants = async (productId) => {
        try {
            setVariantsLoading(true);
            const variants = await getProductVariants(productId);
            const outOfStock = variants?.[0].variants?.filter(
                (variant) => variant?.stock || variant?.inventory_level <= 0
            );
            setVariants(variants?.[0].variants);
            setVariantsOutOfStock(outOfStock);
            setShowVariants(true);
        } catch (error) {
            console.error(error);
        } finally {
            setVariantsLoading(false);
        }
    };

    const addToCart = () => {
        startTransition(async () => {
            try {
                const res = await quickAddToCartFromOverlay(
                    selectedVariant?.product_id,
                    selectedVariant?.id,
                    1
                );
                if (res?.status === 422) {
                    toast(LIMITED_STOCK_MESSAGE, {
                        type: "error",
                        toastId: LIMITED_STOCK_MESSAGE,
                    });
                } else if (res?.status === 404) {
                    toast("Cart Does not Exist", {
                        type: "error",
                        toastId: "Cart Does not Exist",
                    });
                } else if (res) {
                    toast(UPDATE_CART_SUCCESSFULLY, {
                        type: "success",
                        toastId: UPDATE_CART_SUCCESSFULLY,
                    });
                    setShowVariants(false);
                } else throw "Error while updating cart";
            } catch (e) {
                console.error({
                    error: e,
                    origin: "QuickAddToCartOverlay",
                });
                toast(LIMITED_STOCK_MESSAGE, {
                    type: "error",
                    toastId: LIMITED_STOCK_MESSAGE,
                });
            }
        });
    };

    return (
        <>
            <div className={`${showVariants ? "hidden" : "flex"}`}>
                {variantsLoading ? (
                    <LoadingDots className="bg-black" />
                ) : (
                    <Image
                        onClick={(e) => {
                            e.stopPropagation();
                            fetchVariants(productId);
                        }}
                        src={icon}
                        height={20}
                        width={20}
                        alt="eye"
                        className="mx-auto"
                    />
                )}
            </div>

            {/* Mobile View */}
            {showVariants && (
                <div className="z-20 block sm:hidden">
                    <QuickAddToCartPopup
                        totalStockOfVariants={totalStockOfVariants}
                        showCarousel={showCarousel}
                        currencyCode={currencyCode}
                        price={price}
                        productName={productName}
                        selectedVariant={selectedVariant}
                        setSelectedVariant={setSelectedVariant}
                        setShowVariants={setShowVariants}
                        sizeStandard={sizeStandard}
                        variants={variants}
                        variantsOutOfStock={variantsOutOfStock}
                        imagesCollection={imagesCollection}
                        brandName={brandName}
                        salesPrice={salesPrice}
                        isWishListItem={isWishListItem}
                        addProductToWishList={addProductToWishList}
                        removeProductToWishList={removeProductToWishList}

                    />
                </div>
            )}

            {/* Desktop View */}
            {showVariants && (
                <QuickAddToCartPortal>
                    <div className="fixed inset-0 z-[1000] items-center justify-center bg-black/50 hidden md:flex ">
                        <div className="bg-white max-w-xl w-full mx-4 shadow-lg p-6 relative">
                            <button
                                onClick={() => setShowVariants(false)}
                                className="absolute top-2 right-3 text-xl font-medium text-gray-700 hover:text-black"
                            >
                                ✕
                            </button>

                            {/* Desktop View */}

                            <div className="grid grid-cols-2 gap-x-4">
                                {/* Image slider */}
                                <ProductCarousel
                                    productName={productName}
                                    imagesCollection={imagesCollection}
                                    showCarousel={showCarousel}
                                />

                                {/* Product Details */}
                                <div className="relative flex flex-col justify-between">
                                    <div className="mt-10">
                                        <div className="flex justify-between items-center">
                                            <Heading
                                                decoration={"underline"}
                                                fontWeight={"bold"}
                                                customStyle={
                                                    "text-sm sm:text-base"
                                                }
                                                text={brandName}
                                                color={"New_Primary_Default"}
                                            />

                                            <div>
                                                {!isWishListItem ? (
                                                    <Image
                                                        onClick={
                                                            addProductToWishList
                                                        }
                                                        src={EmptyHeart}
                                                        width={24}
                                                        height={24}
                                                        alt="filled-heart"
                                                    />
                                                ) : (
                                                    <Image
                                                        onClick={
                                                            removeProductToWishList
                                                        }
                                                        src={FilledHeart}
                                                        width={24}
                                                        height={24}
                                                        alt="filled-heart"
                                                    />
                                                )}
                                            </div>
                                        </div>
                                        <TypographyPoppins
                                            align="left"
                                            customStyle="text-xs sm:text-base uppercase"
                                            fontWeight="light"
                                            text={productName}
                                        />

                                        <div className="flex items-center">
                                            {salesPrice && (
                                                <TypographyPoppins
                                                    align="left"
                                                    color={"danger"}
                                                    customStyle="text-xs sm:text-sm mr-3"
                                                    fontWeight="normal"
                                                    text={salesPrice}
                                                />
                                            )}
                                            {price && (
                                                <TypographyPoppins
                                                    align="left"
                                                    customStyle={`text-xs sm:text-sm ${salesPrice &&
                                                        "line-through"
                                                        }`}
                                                    fontWeight="normal"
                                                    text={formatPrice(
                                                        price,
                                                        currencyCode
                                                    )}
                                                />
                                            )}
                                        </div>

                                        <LowStockIndicator
                                            remainingStock={
                                                totalStockOfVariants
                                            }
                                        />

                                        <QuickAddToCartOverlay
                                            selectedVariant={selectedVariant}
                                            setSelectedVariant={
                                                setSelectedVariant
                                            }
                                            setShowVariants={setShowVariants}
                                            sizeStandard={sizeStandard}
                                            variants={variants}
                                        />
                                    </div>

                                    <NewPrimaryButton
                                        disabled={!selectedVariant}
                                        processing={isPending}
                                        onClick={() => addToCart()}
                                        width={"w-full"}
                                        text={"Add to Cart"}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </QuickAddToCartPortal>
            )}
        </>
    );
};

export default QuickAddToCartButton;
